<!-- ——— Hero ——— -->
<section class="ts-hero" aria-labelledby="ts-hero-title">
  <div class="ts-hero__inner">
    <div class="ts-hero__copy">
      <p class="t-meta ts-hero__eyebrow">An AI writing studio for storytellers</p>
      <h1 id="ts-hero-title" class="ts-hero__title">
        Every tale begins with
        <em class="ts-hero__em">a&nbsp;single thread.<svg class="ts-hero__thread" viewBox="0 0 300 24" preserveAspectRatio="none" aria-hidden="true"><path d="M4,14 C48,6 92,20 140,12 C186,5 232,8 262,14 C276,17 288,15 296,9"/></svg></em>
      </h1>
      <p class="ts-hero__lede">
        Talespinner writes beside you in your genre, your voice and your language, and keeps
        your characters, your world and your outline in mind with every chapter.
        You hold the pen. It helps you reach the last page.
      </p>
      <div class="ts-hero__actions">
        @if (userIsLoggedIn) {
          <a class="ts-btn ts-btn--primary ts-btn--lg" [routerLink]="['/dashboard']">
            Continue writing <span class="ts-btn__arrow" aria-hidden="true">→</span>
          </a>
        } @else {
          <a class="ts-btn ts-btn--primary ts-btn--lg" [routerLink]="['/account/auth/signup']">
            Start your story <span class="ts-btn__arrow" aria-hidden="true">→</span>
          </a>
        }
        <a class="ts-btn ts-btn--ghost ts-btn--lg" [routerLink]="['/discover']">Read what others wrote</a>
      </div>
      <ul class="ts-hero__facts t-caption">
        <li>Free Quills on sign-up</li>
        <li>No subscription</li>
        <li>Any language</li>
      </ul>
    </div>

    <div class="ts-hero__art">
      <app-hero-window></app-hero-window>
    </div>
  </div>
</section>

<!-- ——— How it works ——— -->
<section class="ts-section ts-how" aria-labelledby="ts-how-title">
  <div class="ts-shell">
    <header class="ts-head" tsReveal>
      <p class="t-meta ts-head__eyebrow">§ How it works</p>
      <h2 id="ts-how-title" class="ts-head__title">From first spark to finished book.</h2>
      <p class="ts-head__lede">Begin wherever the idea is strongest. Talespinner fills in the rest, but only when you ask.</p>
    </header>

    <app-studio-tour [modelName]="featuredModelName" tsReveal></app-studio-tour>

    <p class="ts-how__more"><a class="ts-link" [routerLink]="['/features']">Every feature, stage by stage →</a></p>
  </div>
</section>

<!-- ——— Models & pricing ——— -->
<section class="ts-section ts-pen" aria-labelledby="ts-pen-title">
  <div class="ts-shell ts-pen__grid" tsReveal>
    <div class="ts-pen__models">
      <header class="ts-head">
        <p class="t-meta ts-head__eyebrow">§ Your pen</p>
        <h2 id="ts-pen-title" class="ts-head__title">Choose the model that suits the scene.</h2>
        <p class="ts-head__lede">Switch between today's leading models at any time: the most literary prose, the longest memory, or simply the lowest cost.</p>
      </header>

      <ol class="ts-contents">
        @for (model of models; track model.value; let i = $index) {
          <li class="ts-contents__row">
            <span class="ts-contents__index">{{ i + 1 }}</span>
            <span class="ts-contents__name">{{ model.name }}</span>
            <span class="ts-contents__leader" aria-hidden="true"></span>
            <span class="ts-contents__note">{{ model.strengths[0]?.label }}</span>
          </li>
        }
      </ol>
    </div>

    <aside class="ts-ledger" aria-labelledby="ts-ledger-title">
      <app-doodle kind="owl" class="ts-ledger__owl"></app-doodle>
      <p class="t-meta ts-ledger__eyebrow">Pricing, plainly</p>
      <h3 id="ts-ledger-title" class="ts-ledger__title">Pay for words, not&nbsp;months.</h3>
      <dl class="ts-ledger__list">
        <div class="ts-ledger__item">
          <dt>Free Quills to begin</dt>
          <dd>You get Quills when you sign up, so you can start writing straight away.</dd>
        </div>
        <div class="ts-ledger__item">
          <dt>One Quill, one cent</dt>
          <dd>Top up when you need to. Quills never expire.</dd>
        </div>
        <div class="ts-ledger__item">
          <dt>The price, before you press</dt>
          <dd>Every generation shows an estimate first, and you're never charged more than its upper limit.</dd>
        </div>
        <div class="ts-ledger__item">
          <dt>No subscription</dt>
          <dd>No monthly fee and no hidden costs.</dd>
        </div>
      </dl>
      <a class="ts-link" [routerLink]="['/pricing']">How pricing works →</a>
    </aside>
  </div>
</section>

<!-- ——— From the shelf (only when there are public books to show) ——— -->
@if (shelfBooks.length > 0) {
  <section class="ts-section ts-shelf" aria-labelledby="ts-shelf-title">
    <div class="ts-shell">
      <header class="ts-head ts-head--split">
        <div>
          <p class="t-meta ts-head__eyebrow">§ From the shelf</p>
          <h2 id="ts-shelf-title" class="ts-head__title">Finished by Talespinners.</h2>
        </div>
        <a class="ts-link" [routerLink]="['/discover']">Browse every book →</a>
      </header>

      <div class="ts-shelf__grid" tsReveal>
        @for (book of shelfBooks; track book.id) {
          <a class="ts-shelf-book" [routerLink]="['/book-project', book.id]">
            <div class="ts-shelf-book__cover">
              @if (shelfCovers[book.id]) {
                <img [src]="shelfCovers[book.id]" [alt]="'Cover of ' + book.title" loading="lazy">
              }
            </div>
            @if (book.genre) {
              <p class="t-meta ts-shelf-book__genre">{{ book.genre }}</p>
            }
            <h3 class="ts-shelf-book__title">{{ book.title }}</h3>
            <p class="ts-shelf-book__author">by {{ book.author }}</p>
          </a>
        }
      </div>
    </div>
  </section>
}

<!-- ——— Closing ——— -->
<section class="ts-closing" aria-labelledby="ts-closing-title">
  <div class="ts-closing__copy" tsReveal>
    <p class="t-meta ts-head__eyebrow">Ready when you are</p>
    <h2 id="ts-closing-title" class="ts-closing__title">Pick up the thread.</h2>
    <p class="ts-closing__lede">One line is enough to start a book. Here's one, if you need it.</p>
  </div>
  <app-story-prompt [userIsLoggedIn]="userIsLoggedIn" tsReveal></app-story-prompt>
</section>
